/**
 * BatEntity — an ambient bat that flies erratically over the maze, can screech,
 * and may attack the player when items are picked up.
 *
 * The bat uses two procedural textures (wings-up / wings-down) swapped via a
 * tween timer to simulate flapping. Flight paths are sinusoidal with random
 * direction changes. Bats fly OVER walls (ignore maze structure).
 *
 * States:
 *   - 'emerging'  : flying outward from a nest before transitioning to flight/attack
 *   - 'flying'    : erratic ambient flight, avoids player, screeches randomly
 *   - 'attacking' : diving toward the player after a pickup event or nest emergence
 *   - 'fleeing'   : retreating erratically after hitting or being repelled
 *   - 'despawning': fading out before destruction
 */

import Phaser from 'phaser';
import { TEX_BAT_WINGS_UP, TEX_BAT_WINGS_DOWN } from '../utils/constants.js';
import {
  BAT_FLIGHT_SPEED,
  BAT_ATTACK_SPEED,
  BAT_FLEE_SPEED,
  BAT_ATTACK_DAMAGE_MIN,
  BAT_ATTACK_DAMAGE_MAX,
  BAT_MIN_DISTANCE_TILES,
  BAT_SCREECH_INTERVAL_MIN,
  BAT_SCREECH_INTERVAL_MAX,
  BAT_SCREECH_MAX,
  BAT_ERRATIC_AMPLITUDE,
  BAT_ERRATIC_FREQUENCY,
  BAT_WING_FLAP_SPEED,
  BAT_DESPAWN_ALPHA_DURATION,
  BAT_NEST_EMERGE_SPEED,
  BAT_NEST_EMERGE_DISTANCE,
  TILE_SIZE,
  AUDIO_BAT_SCREECH,
  AUDIO_BAT_HIT,
} from '../config/game-config.js';
import { getSfxVol } from '../utils/audio-config.js';

/** @enum {string} */
const BatState = Object.freeze({
  EMERGING: 'emerging',
  FLYING: 'flying',
  ATTACKING: 'attacking',
  FLEEING: 'fleeing',
  DESPAWNING: 'despawning',
});

export default class BatEntity extends Phaser.GameObjects.Sprite {
  /**
   * @param {Phaser.Scene} scene
   * @param {number} x          - World x spawn position.
   * @param {number} y          - World y spawn position.
   * @param {object} playerRef  - Reference object `{ x, y }` to the player sprite.
   */
  constructor(scene, x, y, playerRef) {
    super(scene, x, y, TEX_BAT_WINGS_UP);

    this.scene = scene;
    this._playerRef = playerRef;
    this._state = BatState.FLYING;
    this._elapsed = 0;

    // Flight direction (unit vector)
    const angle = Math.random() * Math.PI * 2;
    this._dirX = Math.cos(angle);
    this._dirY = Math.sin(angle);

    // Sinusoidal offset axis (perpendicular to direction)
    this._perpX = -this._dirY;
    this._perpY = this._dirX;

    // Screech tracking
    this._screechCount = 0;
    this._nextScreechTime = 0; // Screech immediately on first tick
    this._activeScreech = null; // Currently playing screech sound instance

    // Wing flap timer
    this._wingsUp = true;
    this._flapAccum = 0;

    // Track whether this bat has already dealt damage
    this._hasHit = false;

    // Emergence state tracking (for nest-spawned bats)
    this._emergeOriginX = 0;
    this._emergeOriginY = 0;
    this._emergeDirX = 0;
    this._emergeDirY = 0;
    this._emergeDistance = 0;
    this._emergeTravelled = 0;
    /** @type {boolean} If true, bat will attack after emergence instead of flying. */
    this._attackAfterEmerge = false;

    // Add to scene and configure rendering
    scene.add.existing(this);
    this.setDepth(200); // Above fog, above everything
    this.setAlpha(0);

    // Pop-in fade
    scene.tweens.add({
      targets: this,
      alpha: 1,
      duration: 300,
      ease: 'Sine.easeIn',
    });
  }

  // ---------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------

  /** @returns {string} Current bat AI state (avoids Phaser's built-in `state` property). */
  get batState() {
    return this._state;
  }

  /** @returns {boolean} True if the bat is still active and in play. */
  get isActive() {
    return this.active && this._state !== BatState.DESPAWNING;
  }

  /**
   * Start the bat in emergence mode — fly outward from a nest origin
   * point before transitioning to normal AI.
   *
   * @param {number} originX     - Nest center world X.
   * @param {number} originY     - Nest center world Y.
   * @param {number} angle       - Outward angle (radians) from the nest.
   * @param {boolean} willAttack - If true, bat attacks the player after emerging.
   */
  startEmerging(originX, originY, angle, willAttack) {
    this._state = BatState.EMERGING;
    this._emergeOriginX = originX;
    this._emergeOriginY = originY;
    this._emergeDirX = Math.cos(angle);
    this._emergeDirY = Math.sin(angle);
    this._emergeDistance = BAT_NEST_EMERGE_DISTANCE * TILE_SIZE;
    this._emergeTravelled = 0;
    this._attackAfterEmerge = willAttack;

    // Set initial flight direction to match emergence direction
    this._dirX = this._emergeDirX;
    this._dirY = this._emergeDirY;
    this._perpX = -this._dirY;
    this._perpY = this._dirX;
  }

  /**
   * Switch to attack mode — dive toward the player.
   */
  startAttack() {
    if (this._state !== BatState.FLYING) return;
    this._state = BatState.ATTACKING;
    this._stopScreech();
    this._hasHit = false;
  }

  /**
   * Called when the bat is repelled by the Threia Totem (no damage).
   * Switches to flee mode.
   */
  repel() {
    this._state = BatState.FLEEING;
    this._stopScreech();
    this._setFleeDirection();
  }

  /**
   * Main update tick — called by BatSystem each frame.
   * @param {number} _time  - Phaser scene time
   * @param {number} delta  - ms since last frame
   * @returns {{ hit: boolean, damage: number }} Hit result for this frame.
   */
  tick(delta) {
    if (!this.active || !this.scene) return { hit: false, damage: 0 };

    // Wing flap animation
    this._updateFlap(delta);

    switch (this._state) {
      case BatState.EMERGING:
        return this._updateEmerging(delta);
      case BatState.FLYING:
        return this._updateFlying(delta);
      case BatState.ATTACKING:
        return this._updateAttacking(delta);
      case BatState.FLEEING:
        return this._updateFleeing(delta);
      default:
        return { hit: false, damage: 0 };
    }
  }

  /**
   * Begin fade-out and self-destruct.
   */
  despawn() {
    if (this._state === BatState.DESPAWNING) return;
    this._state = BatState.DESPAWNING;
    this._stopScreech();

    if (!this.scene) {
      this.destroy();
      return;
    }

    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      duration: BAT_DESPAWN_ALPHA_DURATION,
      ease: 'Quad.easeIn',
      onComplete: () => {
        this.destroy();
      },
    });
  }

  // ---------------------------------------------------------------
  // Private — State updates
  // ---------------------------------------------------------------

  /**
   * Emergence flight — bat flies outward from its nest origin, with slight
   * wobble, before transitioning to flying or attacking state.
   * @private
   * @param {number} delta
   * @returns {{ hit: boolean, damage: number }}
   */
  _updateEmerging(delta) {
    const speed = BAT_NEST_EMERGE_SPEED * (delta / 1000);
    this._emergeTravelled += speed;

    // Move outward from nest with a slight wobble
    const wobble = Math.sin(this._emergeTravelled * 0.08) * 3 * (delta / 1000);
    this.x += this._emergeDirX * speed + this._perpX * wobble;
    this.y += this._emergeDirY * speed + this._perpY * wobble;

    // Check if emergence distance reached
    if (this._emergeTravelled >= this._emergeDistance) {
      if (this._attackAfterEmerge) {
        // Transition directly to attack mode
        this._state = BatState.ATTACKING;
        this._hasHit = false;
      } else {
        // Transition to normal ambient flight
        this._state = BatState.FLYING;
        this._randomizeDirection();
      }
    }

    return { hit: false, damage: 0 };
  }

  /**
   * Erratic ambient flight. Avoids coming too close to the player.
   * @private
   * @param {number} delta
   * @returns {{ hit: boolean, damage: number }}
   */
  _updateFlying(delta) {
    this._elapsed += delta;
    const speed = BAT_FLIGHT_SPEED * (delta / 1000);

    // Base movement along direction
    let moveX = this._dirX * speed;
    let moveY = this._dirY * speed;

    // Add sinusoidal perpendicular wobble
    const wobble = Math.sin(this._elapsed * BAT_ERRATIC_FREQUENCY) * BAT_ERRATIC_AMPLITUDE * (delta / 1000);
    moveX += this._perpX * wobble;
    moveY += this._perpY * wobble;

    // Avoidance: steer away if too close to the player
    const pDx = this.x - this._playerRef.x;
    const pDy = this.y - this._playerRef.y;
    const pDist = Math.sqrt(pDx * pDx + pDy * pDy);
    const minDist = BAT_MIN_DISTANCE_TILES * TILE_SIZE;

    if (pDist < minDist && pDist > 0) {
      const pushStr = (minDist - pDist) / minDist * speed * 2;
      moveX += (pDx / pDist) * pushStr;
      moveY += (pDy / pDist) * pushStr;
    }

    this.x += moveX;
    this.y += moveY;

    // Random direction changes (~3% chance per frame, scaled by delta)
    if (Math.random() < 0.03 * (delta / 16)) {
      this._randomizeDirection();
    }

    // Screeching
    this._updateScreech(delta);

    return { hit: false, damage: 0 };
  }

  /**
   * Attack dive toward the player.
   * @private
   * @param {number} delta
   * @returns {{ hit: boolean, damage: number }}
   */
  _updateAttacking(delta) {
    const speed = BAT_ATTACK_SPEED * (delta / 1000);
    const dx = this._playerRef.x - this.x;
    const dy = this._playerRef.y - this.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist > 0) {
      this.x += (dx / dist) * speed;
      this.y += (dy / dist) * speed;
    }

    // Wing flap faster during attack
    this._flapAccum += delta * 0.5; // Extra speed

    // Check hit — within half a tile
    const hitDist = TILE_SIZE * 0.6;
    if (dist < hitDist && !this._hasHit) {
      this._hasHit = true;
      const damage = Phaser.Math.Between(BAT_ATTACK_DAMAGE_MIN, BAT_ATTACK_DAMAGE_MAX);

      // Play hit sound
      try {
        this.scene.sound.play(AUDIO_BAT_HIT, { volume: getSfxVol(0.6) });
      } catch (_) { /* audio may be missing */ }

      // Switch to flee
      this._state = BatState.FLEEING;
      this._setFleeDirection();

      return { hit: true, damage };
    }

    return { hit: false, damage: 0 };
  }

  /**
   * Flee erratically away from the player after hit or repel.
   * @private
   * @param {number} delta
   * @returns {{ hit: false, damage: 0 }}
   */
  _updateFleeing(delta) {
    this._elapsed += delta;
    const speed = BAT_FLEE_SPEED * (delta / 1000);

    let moveX = this._dirX * speed;
    let moveY = this._dirY * speed;

    // Add wobble during flee
    const wobble = Math.sin(this._elapsed * BAT_ERRATIC_FREQUENCY * 1.5) * BAT_ERRATIC_AMPLITUDE * 1.3 * (delta / 1000);
    moveX += this._perpX * wobble;
    moveY += this._perpY * wobble;

    this.x += moveX;
    this.y += moveY;

    return { hit: false, damage: 0 };
  }

  // ---------------------------------------------------------------
  // Private — Helpers
  // ---------------------------------------------------------------

  /**
   * Stop any currently playing screech sound immediately.
   * @private
   */
  _stopScreech() {
    if (this._activeScreech) {
      try {
        this._activeScreech.stop();
        this._activeScreech.destroy();
      } catch (_) { /* ignore */ }
      this._activeScreech = null;
    }
  }

  /**
   * Toggle between wings-up and wings-down textures.
   * @private
   * @param {number} delta
   */
  _updateFlap(delta) {
    this._flapAccum += delta;
    if (this._flapAccum >= BAT_WING_FLAP_SPEED) {
      this._flapAccum -= BAT_WING_FLAP_SPEED;
      this._wingsUp = !this._wingsUp;
      this.setTexture(this._wingsUp ? TEX_BAT_WINGS_UP : TEX_BAT_WINGS_DOWN);
    }
  }

  /**
   * Play a screech sound at random intervals, with volume based on
   * distance to the player (louder when closer).
   * @private
   * @param {number} delta
   */
  _updateScreech(delta) {
    // Update volume of currently playing screech based on distance
    if (this._activeScreech && this._activeScreech.isPlaying) {
      this._activeScreech.setVolume(this._distanceVolume());
    }

    if (this._screechCount >= BAT_SCREECH_MAX) return;

    this._nextScreechTime -= delta;
    if (this._nextScreechTime <= 0) {
      this._screechCount++;
      this._nextScreechTime = this._randomScreechDelay();

      // Stop & destroy any previous screech still playing
      this._stopScreech();

      try {
        const snd = this.scene.sound.add(AUDIO_BAT_SCREECH, {
          volume: this._distanceVolume(),
        });
        this._activeScreech = snd;
        snd.play();
        snd.once('complete', () => {
          if (this._activeScreech === snd) this._activeScreech = null;
          snd.destroy();
        });
      } catch (_) { /* audio file may be missing */ }
    }
  }

  /**
   * Compute screech volume based on distance to the player.
   * Close = full volume, far = very quiet. Uses FOG_REVEAL_RADIUS as
   * the "full volume" range and fades linearly out to ~8 tiles.
   * @private
   * @returns {number} Volume value for Phaser sound.
   */
  _distanceVolume() {
    const baseSfx = getSfxVol(0.4);
    if (!this._playerRef) return baseSfx;

    const dx = this.x - this._playerRef.x;
    const dy = this.y - this._playerRef.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    // Within ~3 tiles: full volume. Beyond ~8 tiles: minimum (10%).
    const nearDist = 3 * TILE_SIZE;
    const farDist = 8 * TILE_SIZE;

    if (dist <= nearDist) return baseSfx;
    if (dist >= farDist) return baseSfx * 0.1;

    // Linear interpolation between full and 10%
    const t = (dist - nearDist) / (farDist - nearDist);
    return baseSfx * (1 - t * 0.9);
  }

  /**
   * Compute a random delay before the next screech.
   * @private
   * @returns {number} ms
   */
  _randomScreechDelay() {
    return BAT_SCREECH_INTERVAL_MIN + Math.random() * (BAT_SCREECH_INTERVAL_MAX - BAT_SCREECH_INTERVAL_MIN);
  }

  /**
   * Randomize the flight direction and perpendicular axis.
   * @private
   */
  _randomizeDirection() {
    const angle = Math.random() * Math.PI * 2;
    this._dirX = Math.cos(angle);
    this._dirY = Math.sin(angle);
    this._perpX = -this._dirY;
    this._perpY = this._dirX;
  }

  /**
   * Set flee direction — away from the player with some randomness.
   * @private
   */
  _setFleeDirection() {
    const dx = this.x - this._playerRef.x;
    const dy = this.y - this._playerRef.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > 0) {
      // Mostly away from player, with random offset
      const awayAngle = Math.atan2(dy, dx);
      const jitter = (Math.random() - 0.5) * Math.PI * 0.5; // ±45 degrees
      const angle = awayAngle + jitter;
      this._dirX = Math.cos(angle);
      this._dirY = Math.sin(angle);
    } else {
      this._randomizeDirection();
    }
    this._perpX = -this._dirY;
    this._perpY = this._dirX;
  }

  /**
   * Clean up on destroy.
   * @param {boolean} [fromScene]
   */
  destroy(fromScene) {
    // Stop and destroy any screech sound still playing
    this._stopScreech();
    this._playerRef = null;
    super.destroy(fromScene);
  }
}
